/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { useState, useEffect } from 'react';
import classNames from 'classnames/bind';
import { days, debounce } from 'common/utils';
import { DateFormat } from 'common/types';
import { withPriceSeperator } from '@flight-workspace/common-client/src/util/number';
import { MutableRefObject } from 'react';
import styles from 'common/modal/chart/domestic/chartBar.module.scss';
import { TripType } from 'contexts/searchParams';
import isPc from '@flight-workspace/common-client/src/util/isPc';

const cx = classNames.bind(styles);

export const mobileDeviceWidth = 1089;

export interface IDailyPrice {
  date: string;
  price: number;
}
interface IDatePeriod {
  idx: number;
  start: Date;
  end: Date;
}

export enum DirectionType {
  LEFT = 'left',
  RIGHT = 'right',
  NONE = 'none',
}

export enum LoadingStatus {
  EXCEPT = -2, // 2, 1일전
  INIT = -1, // 초기값
  LOADING = 0, // 로딩중
  COMPLETE = 1, // 완료
}

export interface IChartOptions {
  barShowLength: number;
  dataList: IChartBarData[];
  maxPrice: number;
  smxp: number;
}

export interface IChartBarData {
  idx: number;
  price: number | null;
  date: Date;
  dateStr: string;
  state: LoadingStatus;
}

export const areaCode: string = 'grp';

export enum TimeCategory {
  DAY = 'DAY',
  HOUR_6_9 = 'HOUR_6_9',
  HOUR_9_12 = 'HOUR_9_12',
  HOUR_12_15 = 'HOUR_12_15',
  HOUR_15_18 = 'HOUR_15_18',
  HOUR_18_21 = 'HOUR_18_21',
  HOUR_21_24 = 'HOUR_21_24',
}

export const timeCategoryCheckList = [
  TimeCategory.DAY,
  TimeCategory.HOUR_6_9,
  TimeCategory.HOUR_9_12,
  TimeCategory.HOUR_12_15,
  TimeCategory.HOUR_15_18,
  TimeCategory.HOUR_18_21,
  TimeCategory.HOUR_21_24,
];

// 다음 or 이전 챠트 데이타를 가져오기 위해
export function getCallItem(
  chartOptions: any,
  dateLength: number,
  callDate: Date,
  idx: number,
  direct: DirectionType,
) {
  const { dataList } = chartOptions.current;
  let item: IDatePeriod;
  if (direct === DirectionType.RIGHT) {
    item = {
      idx,
      start: callDate,
      end: days(callDate).add(dateLength, 'day').toDate(),
    };
    // 마지막날보다 크면
    if (item.end.getTime() > dataList[dataList.length - 1].date.getTime()) {
      item.end = dataList[dataList.length - 1].date;
      item.start = days(item.end).add(-dateLength, 'day').toDate();
      item.idx = getDateToIndex(dataList, item.start);
    }
  } else {
    // left
    const endDate: Date = days(callDate).add(-dateLength, 'day').toDate();
    item = {
      end: callDate,
      start: endDate,
      idx: getDateToIndex(dataList, endDate),
    };
    // item.start 가 내일보다 이전일때 item.start 는 내일이다.
    if (item.start.getTime() < dataList[2].date.getTime()) {
      item.idx = 2;
      item.start = dataList[2].date;
      item.end = days(item.start).add(dateLength, 'day').toDate();
    }
  }
  return item;
}

// 챠트에 date -> index
export function getDateToIndex(dataList: any, date: Date) {
  const startDate = days(date).format(DateFormat.YMD);
  const targetItem = dataList.find(
    (item: any) => item.dateStr === startDate,
  ) || { idx: 0 };
  return targetItem.idx;
}

// 상태를 0으로 로딩 상태로 설정한다
export function setChartStateLoading(
  dataList: any,
  idx: number,
  resultLength: number,
) {
  for (let i = idx; i < idx + resultLength; i++) {
    if (dataList[i] && dataList[i].state >= LoadingStatus.INIT) {
      // -2 인 그제, 어제는 빼고, -1 초기 생성했을때 값. 0 로딩, 1 로딩끝
      dataList[i].state = LoadingStatus.LOADING;
    }
  }
}

// date(20181002) 를 key, value 로 map 생성
export const getDateArrayToMap = (arr: any) => {
  return arr.reduce((result: any, item: any) => {
    result[item.date] = item;
    return result;
  }, {});
};

export const getStops = (selected: string) => {
  if (selected === 'all') {
    // 전체
    return [0, 1, 2];
  }
  if (selected === 'direct') {
    // 직항
    return [0];
  }
  if (selected === 'via1') {
    // 경유 1회 이하(직항 + 경유1회)
    return [0, 1];
  }
  if (selected === 'via2') {
    // 경유 2회 이하(직항 + 경유1회 + 경유2회)
    return [0, 1, 2];
  }
  return [];
};

// stops 에 맞춰 가장 작은 가격을 리턴
function getFaresMin(item: any, sel: any) {
  const { fares } = item;
  let min: any = null;
  fares.forEach((fare: any) => {
    sel.forEach((num: number) => {
      if (num === fare.stops) {
        if (min === null) {
          min = fare.fare ? fare.fare : null;
        } else if (fare.fare < min) {
          min = fare.fare;
        }
      }
    });
  });
  return min;
}

// 가져온 데이타로 chartDataList 설정, max 까지 설정
export function setChartDataList(
  resultData: any,
  directFilter: boolean,
  resultLength: number,
  callItem: any,
  chartOptions: any,
) {
  const { dataList, maxPrice } = chartOptions.current;
  const stops = directFilter ? getStops('direct') : getStops('all'); // direct 직항만 필터
  const pullList = dataList.slice(callItem.idx, callItem.idx + resultLength);
  const tempList: number[] = [];
  pullList.forEach((item: IChartBarData) => {
    item.state = LoadingStatus.COMPLETE;
    const chartItem = resultData[item.dateStr]; // {20180820: {date: 20180820, fares: []}}
    if (chartItem) {
      const money: number = getFaresMin(chartItem, stops) || 0;
      item.price = money;
      tempList.push(item.price); // max 에
    }
  });
  // 챠트값 max 설정
  const max = Math.max.apply(null, tempList);
  if (max > maxPrice) {
    chartOptions.current.maxPrice = max;
  }
}

export function setChartDataListDomestic(
  dailyPriceMap: Map<string, IDailyPrice>,
  resultLength: number,
  callItem: any,
  chartOptions: any,
) {
  const { dataList, maxPrice } = chartOptions.current;
  const pullList: IChartBarData[] = dataList.slice(
    callItem.idx,
    callItem.idx + resultLength,
  );

  pullList.forEach(item => {
    item.state = LoadingStatus.COMPLETE;
    const chartItem = dailyPriceMap.get(item.dateStr);
    if (chartItem) {
      item.price = chartItem.price;
    }
  });

  const priceList = pullList.map(item => (item.price ? item.price : 0));

  const max = Math.max(...priceList);
  if (max > maxPrice) {
    chartOptions.current.maxPrice = max;
  }
}

// 왕복 355,700원~
export const getToolTip = (trip: string, barList: any) => {
  let toolTipInfo = '';
  let toolTipPrice = '';
  if (barList && barList.length > 0 && barList[2]) {
    if (
      barList[2].state === LoadingStatus.INIT ||
      barList[2].state === LoadingStatus.LOADING
    ) {
      toolTipInfo = '가격을 가져오는 중입니다...';
    } else if (barList[2].state === 1) {
      if (barList[2].price !== null) {
        toolTipInfo = trip === TripType.OW ? '편도' : '왕복';
        toolTipPrice = `${withPriceSeperator(barList[2].price)} 원~`;
      } else {
        toolTipInfo = '가격 조회 필요';
      }
    }
  }
  return { toolTipInfo, toolTipPrice };
};

export const getPriceToolTip = (
  trip: string,
  item: any,
  isDomestic?: boolean,
) => {
  if (!item) return '';
  const { state, price } = item;
  let tripType = '';
  let priceText = '가격 조회 필요';
  if (state === LoadingStatus.INIT || state === LoadingStatus.LOADING) {
    priceText = '가격을 가져오는 중입니다...';
  }
  if (state === LoadingStatus.COMPLETE && price) {
    tripType = trip === TripType.OW || isDomestic ? '편도' : '왕복';
    priceText = `${withPriceSeperator(item.price)} 원~`;
  }
  return (
    <span>
      <i className={cx('type')}>{tripType}</i>
      <strong className={cx('price')}>{priceText}</strong>
    </span>
  );
};

// 모바일용 여행기간 2018.10.02.화 ~ 2018.10.07.일
export const getPeriodInfoStr = (
  trip: string,
  barList: IChartBarData[],
  period: number,
) => {
  if (trip === TripType.OW && barList.length > 2) {
    return days(barList[2].date).format(DateFormat.DotYMDd);
  }
  if (
    (trip === TripType.RT || trip === TripType.EVERYWHERE) &&
    barList.length > 2
  ) {
    const endDate = new Date(
      barList[2].date.getFullYear(),
      barList[2].date.getMonth(),
      barList[2].date.getDate() + period - 1,
    );
    return `${days(barList[2].date).format(DateFormat.DotYMDd)} ~ ${days(
      endDate,
    ).format(DateFormat.DotYMDd)}`;
  }
  return '여행 기간';
};

// pc용 여행기간
export const getPeriodTextForPc = (
  trip: string,
  period: number,
  selectedItem: IChartBarData | null,
) => {
  if (selectedItem && trip === TripType.OW) {
    return days(selectedItem.date).format(DateFormat.DotYMDd);
  }
  if (selectedItem && (trip === TripType.RT || trip === TripType.EVERYWHERE)) {
    return `${days(selectedItem.date).format(DateFormat.DotYMDd)} ~ ${days(
      selectedItem.date,
    )
      .add(period - 1, 'day')
      .format(DateFormat.DotYMDd)}`;
  }
  return '여행 기간';
};

export const getInitChartDataList = (today: Date, totalLength: number) => {
  const list = new Array(totalLength).fill(null);
  let date = days(today).toDate();
  return list.map((item: IChartBarData, idx: number) => {
    const chartItem = {
      idx,
      price: null,
      date,
      dateStr: days(date).format(DateFormat.YMD),
      state: idx < 2 ? LoadingStatus.EXCEPT : LoadingStatus.INIT, // -2: 그제, 어제, -1: 오늘부터 다
    };
    date = days(date).add(1, 'day').toDate();
    return chartItem;
  });
};

export const getParams = (
  from: string[],
  to: string,
  trip: string,
  period: number,
  startDate: Date,
) => {
  // const { from, to, trip, period } = chartOptions.current;
  return {
    from,
    to,
    mode:
      trip === TripType.RT || trip === TripType.EVERYWHERE
        ? 'chartRoundTrip'
        : 'chartOneWay',
    departDate: days(startDate).format(DateFormat.YMD),
    hoursBefore: 48,
    roundTripTime: period,
    agent: 100,
  };
};

export const getInitStartIndex = (
  targetDate: Date,
  maxLength: number,
  chartStartDateOffset: number,
) => {
  const index =
    days(targetDate).diff(days().format(DateFormat.YMD), 'day') -
    chartStartDateOffset;
  return index < 0 ? 0 : index > maxLength ? maxLength : index;
};

export const getPullBarList = (
  chartOptions: MutableRefObject<IChartOptions>,
  startIndex: number,
  direction: DirectionType = DirectionType.NONE,
) => {
  const { dataList, barShowLength } = chartOptions.current;
  const pullBarList = dataList.slice(startIndex, startIndex + barShowLength);
  const maxPrice = // max price
    pullBarList.length > 0
      ? pullBarList
          .map((item: IChartBarData) => item.price || 0)
          .reduce((a: number, b: number) => {
            return Math.max(a, b);
          })
      : 0;
  chartOptions.current.maxPrice = maxPrice * 0.1 + maxPrice;
  const tempPullBarList =
    direction === DirectionType.LEFT ? [...pullBarList].reverse() : pullBarList;
  const findItem = tempPullBarList.find(
    (item: IChartBarData) => item.state === LoadingStatus.INIT,
  ) || {
    isCallDate: false,
    idx: -1,
    date: null,
  };
  return { pullBarList, isCallDate: findItem.date, idx: findItem.idx };
};

// get 선택된 bar order index
export const getSelectedBarOrderIndex = (
  targetItem: IChartBarData | undefined | null,
  pullBarList: IChartBarData[],
) => {
  if (!targetItem) return -1;
  return pullBarList.findIndex(
    (item: IChartBarData) => item.idx === targetItem?.idx,
  );
};

export const getSelectedBarList = (
  isMobile: boolean,
  barDataList: IChartBarData[],
  period: number,
  selectedBarIndex: number | null,
) => {
  // 모바일
  if (isMobile) {
    return barDataList[2]
      ? Array(period)
          .fill(0)
          .map((e, i) => i + barDataList[2].idx)
      : [];
  }
  // pc
  return selectedBarIndex
    ? Array(period)
        .fill(0)
        .map((e, i) => i + selectedBarIndex)
    : [];
};

export const getListToMap = (resultData: any) => {
  return resultData?.reduce((acc: any, item: any) => {
    acc[item.date] = item;
    return acc;
  }, {});
};
export const timeout = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const useWindowWidth = (delay: number) => {
  const [windowWidth, setWindowWidth] = useState(0);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    const debounceResize = debounce(handleResize, delay);
    window.addEventListener('resize', debounceResize);
    return () => window.removeEventListener('resize', debounceResize);
  }, []);
  return windowWidth;
};

export const getPcToolTipBoxLimitIndex = (
  barContainerWidth: number,
  barTotalWidth: number,
) => {
  return Math.floor(
    (barContainerWidth - 154) / barTotalWidth, // 154 : 가격툴팁 넓이
  );
};

export const getInitChartStatus = (
  departureDate: string,
  barTotal: number,
  dir: DirectionType,
  chartStartDateOffset: number,
) => {
  return {
    startIndex: getInitStartIndex(
      !departureDate ? new Date() : days(departureDate).toDate(),
      barTotal,
      chartStartDateOffset,
    ),
    direction: dir,
  };
};

export const getHelpLink = (w: number) => {
  return isPc(w)
    ? 'https://help.naver.com/support/contents/contents.help?serviceNo=11270&categoryNo=20346'
    : 'https://m.help.naver.com/support/contents/contentsView.nhn?contentsNo=9381&lang=ko';
};
